/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./MinimoveResult.css";
import { useAppContext } from "../../../../AppContext";
import { base64ToBlob, showAlert } from "../../../../generalUtils";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import {
  FACE_STORE_NAME,
  BODY_STORE_NAME,
  getImageBlobFromDB,
  getFullObjectFromDB,
} from "../../../../IndexedDBUtils";
import {
  extractFaceInfoFromPath,
  calculateFaceStyle,
} from "../../../../Components/Common/FacePositionUtils/faceUtils";

// ──────────────────────────────────────────────────────────────────────────────
//  GIFs that show the current status (same files used by Swingies)
// ──────────────────────────────────────────────────────────────────────────────
import gifSavingResult from "../../../../assets/gifs/result-saving_result.gif";
import gifDone from "../../../../assets/gifs/result-done.gif";
import gifFailed from "../../../../assets/gifs/result-failed.gif";
import { SERVER_URL } from "../../../../config/serverUrl";
import { getEventScope, isCurrentScope } from "../../../../eventIdentity";
import {
  clearClaimState,
  ensureApproveRequest,
  readClaimState,
  writeClaimState,
} from "../../shared/faceClaimClient";
import {
  MSG_FAILED,
  MSG_PROCESSING,
  MSG_STILL_PENDING,
  useApproveStatus,
} from "../../shared/useApproveStatus";
import { MSG_LOST } from "../../shared/useFaceClaim";
import {
  APPROVE_ATTEMPTS,
  APPROVE_BUSY_WAIT_MS,
  MSG_APPROVE_RETRY_OFFLINE,
  approveFailureStatus,
  isApproveBusy,
  reportApproveTiming,
  sendApprove,
  useApproveRetry,
} from "../../shared/approveRetry";
import { prepareResend, RECOVERY } from "../../shared/approveRecovery";
import { getFaceClaimEvent } from "../../shared/faceClaimEvent";
import { MSG_APPROVE_ERROR, MSG_APPROVE_NOT_SENT, showGuestNotice } from "../../shared/guestNotice";
import ApproveRetryNotice from "../../shared/ApproveRetryNotice";
import { returnHomeAfterAdminRelease } from "../../shared/adminRelease";
import { isFlowExpired } from "../../shared/useFlowInactivity";
import FlowCountdown from "../../shared/FlowCountdown";

const MinimoveResultPage = ({ siteRedirect }) => {
  const navigate = useNavigate();
  const { hideLoading, showLoading } = useLoading();
  const { eventName, userId } = useAppContext();
  const serverUrl = SERVER_URL;
  const businessSiteUrl = process.env.REACT_APP_BUSINESS_SITE_URL;

  // ────────────────────────────────────────────────────────────────────────────
  //  Refs
  // ────────────────────────────────────────────────────────────────────────────
  const bodyImageRef = useRef(null);
  const containerRef = useRef(null);
  const faceElementRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────────
  //  State
  // ────────────────────────────────────────────────────────────────────────────
  const [selectedFace, setSelectedFace] = useState(null);
  const [selectedBody, setSelectedBody] = useState(null);
  const [faceStyle, setFaceStyle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [gifUrl, setGifUrl] = useState(null); // status GIF
  // Guards a double-tap on "אשר ושלח" (the button carried no disabled state at
  // all — a second tap fired a second, identical /approve request; the first
  // succeeded, the second 409'd on "face already in use" and showed the guest
  // a scary generic failure despite the photo having landed). A ref because it
  // must be true synchronously on the SECOND tap, before any state update
  // could re-render the disabled button (2026-08-04, live MAAYAN report).
  const approveInFlightRef = useRef(false);
  const [isApproving, setIsApproving] = useState(false);
  // The server answers /approve at ENQUEUE time; "done" is learned by polling the claim status, and the
  // inactivity timer is paused for as long as the image is actually being processed.
  const approveStatus = useApproveStatus();
  // A thrown / aborted approve that the server never recorded is re-sent once silently, then offered as a button.
  const approveRetry = useApproveRetry();

  // Event visit this page belongs to. Redirect timers and the late content
  // load must never act on a different event (or a later visit of this one)
  // that replaced it, so they are tracked, cleared on unmount and re-checked.
  const scopeRef = useRef(getEventScope());
  const timersRef = useRef([]);
  const unmountedRef = useRef(false);
  const scheduleIfCurrent = (fn, ms) => {
    if (unmountedRef.current) return;
    const id = setTimeout(() => {
      if (isCurrentScope(scopeRef.current)) fn();
    }, ms);
    timersRef.current.push(id);
  };

  useEffect(() => {
    // Handle bfcache restoration: when browser restores this page from cache
    // after redirect, reload to load the home URL (set by replaceState)
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    unmountedRef.current = false;

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      unmountedRef.current = true;
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
      if (selectedFace?.url) {
        URL.revokeObjectURL(selectedFace.url);
      }
      if (selectedBody?.url) {
        URL.revokeObjectURL(selectedBody.url);
      }
    };
  }, []);

  const getGifUrl = (name) => {
    switch (name) {
      case "saving":
        return gifSavingResult;
      case "done":
        return gifDone;
      case "failed":
        return gifFailed;
      default:
        return null;
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  //  Load selected face + body from IndexedDB
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // These navigations run after IndexedDB awaits: skip them when this page
    // was replaced by another event/visit in the meantime.
    const loadScope = getEventScope();
    const navigateIfCurrent = (to) => {
      if (isCurrentScope(loadScope)) navigate(to);
    };
    const loadSelectedContent = async () => {
      try {
        showLoading();

        const selectedFacesJson = sessionStorage.getItem("selectedFaces");
        const selectedBodiesJson = sessionStorage.getItem("selectedBodies");

        if (!selectedFacesJson || !selectedBodiesJson) {
          console.warn("No selected face or body found");
          showAlert("err", "לא נמצאו פנים או גוף שנבחרו", 3000);
          navigate(`/${eventName}`, { replace: true });
          return;
        }

        const selectedFaces = JSON.parse(selectedFacesJson);
        const selectedBodies = JSON.parse(selectedBodiesJson);

        if (!selectedFaces.length || !selectedBodies.length) {
          console.warn("Empty selected arrays");
          showAlert("err", "בחירת הפנים או הגוף ריקה", 3000);
          navigate(`/${eventName}`, { replace: true });
          return;
        }

        // Face loading
        const facePath = selectedFaces[0];
        try {
          const faceInfo = extractFaceInfoFromPath(facePath);
          const faceData = await getImageBlobFromDB(
            eventName,
            FACE_STORE_NAME,
            facePath
          );

          if (!faceData) {
            throw new Error("Face data not found in IndexedDB");
          }

          // Make sure we have a Blob and not a string
          const faceBlob =
            typeof faceData === "string"
              ? await base64ToBlob(faceData, "image/webp")
              : faceData;

          const faceUrl = URL.createObjectURL(faceBlob);

          // Load face image dimensions
          await new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => {
              setSelectedFace({
                url: faceUrl,
                faceHeightRatio: faceInfo.faceHeightRatio,
                centerX: faceInfo.centerX,
                centerY: faceInfo.centerY,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
                path: facePath,
              });
              resolve();
            };

            img.onerror = () => {
              console.error("Error loading face image");
              URL.revokeObjectURL(faceUrl);
              reject(new Error("Face image load failed"));
            };

            img.src = faceUrl;
          });
        } catch (faceError) {
          console.error("Error loading face:", faceError);
          showAlert("err", "שגיאה בטעינת הפנים הנבחרים", 3000);
          navigateIfCurrent(`/${eventName}/face-gallery`);
          return;
        }

        // Body loading
        const bodyPath = selectedBodies[0];
        try {
          const bodyObject = await getFullObjectFromDB(
            eventName,
            BODY_STORE_NAME,
            bodyPath
          );

          if (!bodyObject || !bodyObject.imageData) {
            throw new Error("Body data not found in IndexedDB");
          }

          // Make sure face_position_ratio exists
          if (!bodyObject.face_position_ratio) {
            bodyObject.face_position_ratio = { x: 0.5, y: 0.2 };
          }

          // Make sure imageData is a Blob
          const bodyBlob =
            typeof bodyObject.imageData === "string"
              ? await base64ToBlob(bodyObject.imageData, "image/webp")
              : bodyObject.imageData;

          const objectUrl = URL.createObjectURL(bodyBlob);

          setSelectedBody({
            path: bodyPath,
            url: objectUrl,
            face_position_ratio: bodyObject.face_position_ratio,
            width: bodyObject.width || 0,
            height: bodyObject.height || 0,
          });
        } catch (bodyError) {
          console.error("Error loading body:", bodyError);
          showAlert("err", "שגיאה בטעינת הגוף הנבחר", 3000);
          navigateIfCurrent(`/${eventName}/body-gallery`);
          return;
        }
      } catch (err) {
        console.error("Error loading content", err);
        showAlert("err", "שגיאה כללית בטעינת התמונות", 3000);
        navigateIfCurrent(`/${eventName}`);
      } finally {
        setIsLoading(false);
        hideLoading();
      }
    };
    loadSelectedContent();
  }, []); // once

  // ────────────────────────────────────────────────────────────────────────────
  //  Helpers
  // ────────────────────────────────────────────────────────────────────────────
  const handleBodyImageLoad = () => setImagesLoaded(true);

  // Calculate face placement when everything ready (unchanged from original)
  useEffect(() => {
    if (
      selectedFace &&
      selectedBody &&
      bodyImageRef.current &&
      containerRef.current &&
      imagesLoaded
    ) {
      const calc = () => {
        try {
          const style = calculateFaceStyle(
            bodyImageRef.current,
            containerRef.current,
            selectedBody,
            selectedFace,
            true,
            {
              name: "headSwing",
              duration: "1.5s",
              timing: "ease-in-out",
              direction: "alternate",
            },
            0
          );

          // fine‑tune pivot
          const neckPos = Math.min(
            100,
            (selectedFace.centerY + selectedFace.faceHeightRatio / 2) * 100
          );
          if (style) style.transformOrigin = `50% ${neckPos}%`;
          return style;
        } catch (e) {
          console.error("calc error", e);
          return null;
        }
      };

      const initial = calc();
      setFaceStyle(initial);

      const recalcTimer = setTimeout(() => {
        setFaceStyle(calc());
        setTimeout(() => setFaceStyle(calc()), 300);
      }, 150);

      const onResize = () => setFaceStyle(calc());
      window.addEventListener("resize", onResize);

      return () => {
        window.removeEventListener("resize", onResize);
        clearTimeout(recalcTimer);
      };
    }
  }, [selectedFace, selectedBody, imagesLoaded]);

  // ────────────────────────────────────────────────────────────────────────────
  //  Navigation helpers
  // ────────────────────────────────────────────────────────────────────────────
  const handleBack = () => navigate(`/${eventName}/body-gallery`);

  // ────────────────────────────────────────────────────────────────────────────
  //  APPROVE → send only Dropbox paths
  // ────────────────────────────────────────────────────────────────────────────
  // The approve ended without an outcome the guest can act on (the claim failed, or it was lost and cannot be sent
  // again in this visit, e.g. the event ended): the failed screen with a Hebrew message, then Home after 6 s.
  const endLost = (terminal) => {
    clearClaimState(eventName);
    setGifUrl(getGifUrl("failed"));
    showGuestNotice(terminal === "failed" ? MSG_FAILED : MSG_APPROVE_NOT_SENT, 6000);
    scheduleIfCurrent(() => navigate(`/${eventName}`), 6000);
  };

  // Definite failure / lost selection: the failed screen with a Hebrew explanation, then the face gallery.
  const leaveToGallery = (message) => {
    clearClaimState(eventName);
    approveStatus.abandon();
    setGifUrl(getGifUrl("failed"));
    showGuestNotice(message, 6000);
    scheduleIfCurrent(
      () => navigate(`/${eventName}/face-gallery`, { state: { error: message } }),
      6000
    );
  };

  // A guest tap on "אשר ושלח". Everything that sends lives in attemptApprove, which the one silent re-send and
  // the "נסה שוב" button run again for the SAME request (same request id, same claim token).
  const handleApprove = async () => {
    if (approveInFlightRef.current) return; // a tap already in flight — ignore
    // Expired flow (latched synchronously by the capture-phase activity handler of THIS dispatch) or a
    // visit that is already over: no new work, no request, no shared write.
    if (isFlowExpired() || unmountedRef.current || !isCurrentScope(scopeRef.current)) return;
    approveInFlightRef.current = true;
    setIsApproving(true);
    // Persisted BEFORE sending: a lost response / reload re-poll repeats THIS approve, never a new one.
    const { approveRid, approveAt } = ensureApproveRequest(eventName);
    const claimState = readClaimState(eventName) || { approveRid, approveAt };
    const request = { approveRid, claimToken: claimState.token || undefined, claimState };
    approveRetry.begin(request, approveAt);
    await attemptApprove(request);
  };

  // The "נסה שוב" button of the lost screen. One tap = one recovery + re-send; a double tap finds the offer already
  // taken. Offered for as long as the flow lives (its 90 s deadline ends the visit), whatever lease is left.
  const handleRetryApprove = async () => {
    if (isFlowExpired() || unmountedRef.current || !isCurrentScope(scopeRef.current)) return;
    const request = approveRetry.takeOffer();
    if (!request) return;
    await attemptApprove(request, { recover: true, attempt: APPROVE_ATTEMPTS.BUTTON });
  };

  // Sends the approve once (bounded) and acts on the outcome. The body is paths only, so a re-send rebuilds the
  // same body from sessionStorage (MiniMove has no localStorage.finalImage). `recover` = this repeats a lost approve
  // (the silent re-send, or the "נסה שוב" button): approveRecovery.prepareResend() first renews the hold (the same
  // request) or acquires the same faces again (a NEW request). A repeat of the same request may meet an earlier
  // attempt still in server intake (409 busy). `timeoutMs` is the bound left by the editing lease (absent = the full
  // APPROVE_TIMEOUT_MS); `attempt` names the row in approve_timings.csv.
  const attemptApprove = async (
    initialRequest,
    { resend = false, timeoutMs, attempt = APPROVE_ATTEMPTS.FIRST, recover = false } = {}
  ) => {
    let request = initialRequest;
    let lease = null;
    // Processing time starts now: pause the flow inactivity timer until the outcome is known.
    approveStatus.hold();
    // Same visit guard as the redirect timers: nothing below touches state once the visit is over.
    const scope = scopeRef.current;
    const stillCurrent = () => !unmountedRef.current && isCurrentScope(scope);

    // show waiting GIF
    setGifUrl(getGifUrl("saving"));

    // Set once the request is about to go out: a send that failed before that has nothing to report.
    let requestStartMs = null;
    let timingReported = false;
    // The client half of approve_timings.csv for THIS attempt (answered, aborted or thrown): one row per attempt.
    const reportTiming = (status) => {
      timingReported = true;
      reportApproveTiming({
        serverUrl,
        eventName,
        attraction: "minimove",
        userId,
        requestStartMs,
        payloadKb: 0, // paths only — the compose inputs live server-side
        status,
        attempt,
        lease,
      });
    };

    // The approve got no usable answer (aborted, thrown, or a re-send that found the earlier attempt still in
    // intake). The response may have been lost AFTER the server accepted it: ask the claim by the persisted
    // request id before declaring a failure (410 = never accepted = the old behavior).
    // The ORIGINAL captured visit decides first: a late rejection from event A after A -> B (or A -> B -> A,
    // or leaving this result page) must not pause B's flow or send A's token/request id under B.
    const pollUnansweredApprove = () => {
      if (!stillCurrent()) return;
      approveStatus.start({
        requestId: request.approveRid,
        claimToken: request.claimToken,
        scope,
        onTerminal: (terminal) => {
          if (!stillCurrent()) return;
          if (terminal === "lost") {
            // 410 claim_unknown: the server never recorded this request, so it is safe to send it again. The first
            // loss re-sends silently (after renewing the hold or acquiring the faces again; same paths); a later
            // loss shows the "נסה שוב" button, whatever lease is left (KARNI 2026-10-25: losses at ~91 s used to get
            // neither). The flow's own 90 s deadline ends the visit.
            if (approveRetry.takeSilentResend()) {
              attemptApprove(request, { recover: true, attempt: APPROVE_ATTEMPTS.AUTO });
              return;
            }
            clearClaimState(eventName);
            setGifUrl(getGifUrl("failed"));
            approveRetry.offer();
            return;
          }
          if (terminal === "done") {
            clearClaimState(eventName);
            setGifUrl(getGifUrl("done"));
            scheduleIfCurrent(() => {
              sessionStorage.removeItem("selectedFaces");
              sessionStorage.removeItem("selectedBodies");
              navigate(`/${eventName}`);
            }, 6000);
          } else {
            endLost(terminal);
          }
        },
      });
    };

    if (recover) {
      // A lost approve is repeated only on a hold with enough lease left (see approveRecovery.js).
      const outcome = await prepareResend({
        event: getFaceClaimEvent(),
        request,
        leaseEndAt: approveRetry.leaseEnd(),
        isLive: stillCurrent,
      });
      if (!stillCurrent()) return;
      if (outcome.kind === RECOVERY.PROCESSING) {
        // The approve was recorded after all: its claim is processing, ask it instead of sending.
        pollUnansweredApprove();
        return;
      }
      if (outcome.kind === RECOVERY.REVOKED) {
        approveStatus.abandon();
        clearClaimState(eventName);
        returnHomeAfterAdminRelease(navigate);
        return;
      }
      if (outcome.kind === RECOVERY.TAKEN) {
        leaveToGallery(MSG_LOST);
        return;
      }
      if (outcome.kind === RECOVERY.ENDED) {
        approveStatus.abandon();
        endLost("lost");
        return;
      }
      if (outcome.kind === RECOVERY.OFFLINE) {
        // Neither the renewal nor the acquisition got through: keep the button (the next tap tries again).
        approveRetry.adopt(outcome.request || request, approveRetry.leaseEnd());
        approveStatus.abandon();
        clearClaimState(eventName);
        setGifUrl(getGifUrl("failed"));
        approveRetry.offer(MSG_APPROVE_RETRY_OFFLINE);
        return;
      }
      // Any repeat after a loss may meet an earlier attempt still in server intake (409 busy): wait and poll,
      // never read it as "faces in use" (also after a re-acquire, whose request id is new).
      resend = true;
      request = outcome.request;
      timeoutMs = outcome.timeoutMs;
      lease = outcome.lease;
      approveRetry.adopt(request, outcome.leaseEndAt);
      // The lost screen cleared the persisted claim record; the request is persisted again BEFORE sending, as at
      // the first tap: a reload while it is processed repeats it. A record that exists by now is never overwritten.
      if (!readClaimState(eventName)) writeClaimState(eventName, request.claimState);
    }
    const { approveRid, claimToken } = request;

    try {
      const selectedFaces =
        JSON.parse(sessionStorage.getItem("selectedFaces")) || [];
      const selectedBodies =
        JSON.parse(sessionStorage.getItem("selectedBodies")) || [];

      requestStartMs = performance.now();
      // Bounded (APPROVE_TIMEOUT_MS, or the lease left for a re-send): an abort or a thrown fetch lands in the catch below.
      const { response, data } = await sendApprove({
        url: `${serverUrl}/approve?event_name=${eventName}`,
        timeoutMs,
        tolerateBadBody: true,
        body: {
          image: null,
          selected_faces: selectedFaces, // ONLY PATHS
          selected_bodies: selectedBodies, // ONLY PATHS
          UserId: userId,
          event_name: eventName,
          request_id: approveRid,
          ...(claimToken ? { claim_token: claimToken } : {}),
        },
      });

      // Fire-and-forget: the client half of approve_timings.csv. Never blocks
      // or fails the guest flow — the worker owns the server half.
      reportTiming(response.status);

      // Release object URLs
      if (selectedFace?.url) URL.revokeObjectURL(selectedFace.url);
      if (selectedBody?.url) URL.revokeObjectURL(selectedBody.url);

      if (!stillCurrent()) return;

      const onApproved = () => {
        clearClaimState(eventName);
        setGifUrl(getGifUrl("done"));
        // Redirect to business site if enabled and URL is configured
        // Using replace() to prevent browser Back returning to result page
        scheduleIfCurrent(() => {
          sessionStorage.removeItem("selectedFaces");
          sessionStorage.removeItem("selectedBodies");
          if (siteRedirect !== false && businessSiteUrl) {
            window.history.replaceState(null, "", `/${eventName}`);
            window.location.href = businessSiteUrl;
          } else {
            navigate(`/${eventName}`);
          }
        }, 6000);
      };

      if (response.ok && data.success) {
        if (data.code && data.code !== "done") {
          // queued / running: poll the claim (timer paused meanwhile)
          approveStatus.start({
            requestId: approveRid,
            claimToken,
            scope,
            onTerminal: (terminal) => {
              if (!stillCurrent()) return;
              if (terminal === "done") onApproved();
              else leaveToGallery(terminal === "failed" ? MSG_FAILED : MSG_LOST);
            },
          });
        } else {
          // `done` (synchronous fallback) or a server that predates claims: finished now
          approveStatus.abandon();
          onApproved();
        }
      } else if (data.code === "claim_revoked") {
        // The operator released this guest's head before the approve landed: start over at Home.
        approveStatus.abandon();
        clearClaimState(eventName);
        returnHomeAfterAdminRelease(navigate);
      } else if (
        response.status === 410 ||
        // A claim-layer refusal (expired, swapped, processing, bound to another guest id, request mismatch): the
        // hold is unusable for THIS guest, so the selection is lost (gallery). result_pending and the legacy
        // answers keep the generic Home path below.
        (response.status === 409 && data.code && !["faces_held", "result_pending"].includes(data.code)) ||
        ["claim_expired", "faces_mismatch", "processing"].includes(data.code)
      ) {
        leaveToGallery(MSG_LOST);
      } else if (resend && isApproveBusy(response, data)) {
        // A re-send met the earlier attempt of this same approve still in server intake (it holds the per-guest
        // lock and has recorded nothing yet): not a failed approve. Give the intake a moment to finish, then ask
        // the claim by the same request id; the processing hold stays on meanwhile.
        scheduleIfCurrent(pollUnansweredApprove, APPROVE_BUSY_WAIT_MS);
      } else {
        approveStatus.abandon();
        clearClaimState(eventName);
        setGifUrl(getGifUrl("failed"));
        showGuestNotice(MSG_APPROVE_ERROR, 6000);
        scheduleIfCurrent(() => navigate(`/${eventName}`), 6000);
      }
    } catch (err) {
      console.error(err);
      // A request that went out and was aborted (APPROVE_TIMEOUT_MS) or threw is on record too; the event and
      // guest are this attempt's own, like the answered report above (an answered one is never reported twice).
      if (requestStartMs !== null && !timingReported) reportTiming(approveFailureStatus(err));
      // Release object URLs
      if (selectedFace?.url) URL.revokeObjectURL(selectedFace.url);
      if (selectedBody?.url) URL.revokeObjectURL(selectedBody.url);
      pollUnansweredApprove();
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  //  Render
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="minimove-result-container">
      <h1 className="minimove-result-title rtl">אשרו את בחירתכם</h1>
      <FlowCountdown variant="fixed" />

      {/* preview area */}
      <div className="minimove-result-image-wrapper" ref={containerRef}>
        {!isLoading && selectedBody ? (
          <>
            <img
              ref={bodyImageRef}
              src={selectedBody.url}
              alt="Selected Body"
              className="minimove-result-image"
              onLoad={handleBodyImageLoad}
              onError={(e) => {
                console.error("Body load err");
                e.target.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' fill='%23cccccc'%3E%3Ctext x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23333333' font-family='sans-serif' font-size='24'%3EError%3C/text%3E%3C/svg%3E";
                handleBodyImageLoad();
              }}
            />

            {selectedFace && faceStyle && imagesLoaded && (
              <div
                ref={faceElementRef}
                className="face-overlay animated-face"
                style={faceStyle}
              />
            )}

            {gifUrl && (
              <img
                src={gifUrl}
                alt="status"
                className="minimove-result-status-gif"
              />
            )}
            {(approveStatus.phase === "processing" ||
              approveStatus.phase === "pending") && (
              <p
                className="rtl"
                data-testid="approve-status-text"
                style={{ textAlign: "center", fontWeight: 700 }}
              >
                {approveStatus.phase === "pending"
                  ? MSG_STILL_PENDING
                  : MSG_PROCESSING}
              </p>
            )}
          </>
        ) : (
          <div className="minimove-result-placeholder">
            <div className="minimove-result-placeholder-text">
              טוען תצוגה מקדימה...
            </div>
          </div>
        )}
      </div>

      {approveRetry.offered && (
        <ApproveRetryNotice onRetry={handleRetryApprove} message={approveRetry.offerMessage} />
      )}

      {/* footer buttons */}
      <div className="minimove-result-button-container">
        <button className="minimove-result-footer-button" onClick={handleBack}>
          חזרה
        </button>
        <button
          className="minimove-result-footer-button"
          onClick={handleApprove}
          disabled={isApproving}
        >
          אשר ושלח
        </button>
      </div>
    </div>
  );
};

export default MinimoveResultPage;
